// src/App.jsx
import React from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import Home from './pages/Home.jsx';
import Shop from './pages/Shop.jsx';
import Product from './pages/Product.jsx';
import './index.css';
import Gallery from './pages/Gallery.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import Collections from './pages/Collections.jsx';
import SizeGuide from './pages/SizeGuide.jsx';
import Press from './pages/Press.jsx';
import Stockists from './pages/Stockists.jsx';
import ShippingAndReturns from './pages/ShippingAndReturns.jsx';
import Lookbook from './pages/Lookbook.jsx';
import CampaignPage from './pages/CampaignPage.jsx';
import ShopProvider from './context/shopContext.js';
import SearchResultsPage from './pages/SearchResultPage.jsx';

import queryClient from './lib/queryClient';

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import localforage from 'localforage';

import { MarketProvider } from './context/MarketContext.jsx';

import AnnouncementBar from './components/AnnouncementBar.jsx';

// --- Product route: SEO-friendly handle-based URLs
function ProductHandleRoute() {
  const { productHandle, collectionHandle } = useParams();
  // Pass handles into Product; key ensures remount on handle change
  return (
    <Product
      key={productHandle}
      productHandle={productHandle}
      collectionHandle={collectionHandle}
    />
  );
}

// --- Legacy numeric product ID route (your current /product/:id)
function LegacyProductRoute() {
  const { id } = useParams();
  return <Product key={id} legacyId={id} />;
}

// Async persister using localforage
const persister =
  typeof window !== 'undefined'
    ? createAsyncStoragePersister({
        storage: localforage,
        key: 'rq-cache',
        throttleTime: 1000,
      })
    : undefined;

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persister ? { persister } : undefined}
    >
      {/* MarketProvider needs to wrap anything that calls useMarket() */}
      <MarketProvider>
        <ShopProvider>
          <AnnouncementBar />
          <Routes>
            {/* Home */}
            <Route path="/" element={<Home />} />

            {/* Shopify-style collections */}
            <Route path="/collections/all" element={<Shop />} />
            <Route path="/collections/:collectionHandle" element={<Shop />} />

            {/* Shopify-style product URLs */}
            {/* e.g. /collections/all/products/layered-jersey-bra-in-charcoal */}
            <Route
              path="/collections/:collectionHandle/products/:productHandle"
              element={<ProductHandleRoute />}
            />

            {/* Simple product URL */}
            {/* e.g. /products/layered-jersey-bra-in-charcoal */}
            <Route
              path="/products/:productHandle"
              element={<ProductHandleRoute />}
            />

            {/* Legacy numeric-product route (kept for backwards compat) */}
            {/* e.g. /product/15682060124541 */}
            <Route path="/product/:id" element={<LegacyProductRoute />} />

            {/* Content pages */}
            <Route path="/pages/galleries" element={<Gallery />} />
            <Route path="/pages/about-us" element={<About />} />
            <Route path="/pages/contact" element={<Contact />} />
            <Route path="/home" element={<Collections />} />
            <Route path="/pages/size-guide" element={<SizeGuide />} />
            <Route path="/pages/press" element={<Press />} />
            <Route path="/pages/stockists" element={<Stockists />} />
            <Route path="/pages/shipping-and-returns" element={<ShippingAndReturns />} />
            <Route path="/lookbook" element={<Lookbook />} />
            <Route path="/pages/gallery/:campaignId" element={<CampaignPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
          </Routes>
        </ShopProvider>
      </MarketProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
